import { useBaseBusinessStore } from '@/store/base/baseStore'
import { message, notification } from '@/utils/naive-instance'
import { ElMessage } from 'element-plus'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'

type LoginType = 'account' | 'phone'

type CaptchaInfo = Record<
  LoginType,
  {
    img: string
    captchaKey: string
  }
>

export function useLoginForm(options: { loginStore: any }) {
  const { loginStore } = options || {}
  const router = useRouter()
  const baseStore = useBaseBusinessStore('app-api/system')
  const hpFormRef = ref()
  const loading = ref(false)
  const agreementChecked = ref(false)
  const currentActiveName = ref<LoginType>('phone')
  const captchaInfo = ref<CaptchaInfo>({
    account: { img: '', captchaKey: '' },
    phone: { img: '', captchaKey: '' }
  })

  const accountForm = ref({
    account: '',
    password: '',
    captchaCode: '',
    captchaKey: ''
  })

  const mobileForm = ref({
    mobile: '',
    captchaCode: '',
    captchaKey: '',
    smsCode: ''
  })

  const rules = computed(() => ({
    account: [
      {
        required: true,
        message: '请输入身份证号/登录账号/准考证号/手机号登录',
        trigger: ['input', 'blur']
      }
    ],
    password: [
      { required: true, message: '请输入密码', trigger: ['input', 'blur'] }
    ],
    mobile: [
      {
        required: true,
        pattern: /^1[3-9]\d{9}$/,
        message: '手机号格式错误',
        trigger: ['input', 'blur']
      }
    ],
    captchaCode: [
      {
        required: true,
        message: '请输入图形验证码',
        trigger: ['input', 'blur']
      }
    ]
  }))

  const formItems = computed(() => {
    if (currentActiveName.value === 'account') {
      return [
        {
          path: 'account',
          placeholder: '请输入身份证号/登录账号/准考证号/手机号登录',
          type: 'input',
          prefixIcon: 'shouji',
          prefixIconSize: '22px',
          size: 'large',
          bordered: false
        },
        {
          path: 'password',
          placeholder: '请输入密码',
          type: 'password',
          showPasswordOn: 'mousedown',
          prefixIcon: 'mima',
          prefixIconSize: '18px',
          size: 'large',
          bordered: false
        },
        {
          path: 'captchaCode',
          placeholder: '请输入图形验证码',
          type: 'slot',
          slotName: 'captchaCode',
          prefixIcon: 'tupian',
          prefixIconSize: '20px',
          size: 'large',
          bordered: false
        }
      ]
    }

    return [
      {
        path: 'mobile',
        placeholder: '请输入手机号',
        type: 'input',
        prefixIcon: 'shouji',
        prefixIconSize: '22px',
        size: 'large',
        bordered: false
      },
      {
        path: 'captchaCode',
        placeholder: '请输入图形验证码',
        type: 'slot',
        slotName: 'captchaCode',
        prefixIcon: 'tupian',
        prefixIconSize: '20px',
        size: 'large',
        bordered: false
      },
      {
        path: 'smsCode',
        slotName: 'smsCode',
        placeholder: '请输入验证码',
        type: 'slot',
        prefixIcon: 'zhibao',
        prefixIconSize: '24px',
        size: 'large',
        bordered: false
      }
    ]
  })

  const formData = computed(() => {
    return currentActiveName.value === 'account'
      ? accountForm.value
      : mobileForm.value
  })

  const currentCaptchaImg = computed(() => {
    return captchaInfo.value[currentActiveName.value].img
  })

  const fetchCaptcha = async (type: LoginType = currentActiveName.value) => {
    try {
      const res = await baseStore.getRequest('captcha/image', undefined, {
        needAuth: false
      })

      if (res.code === 0) {
        captchaInfo.value[type] = {
          img: res.data.image,
          captchaKey: res.data.captchaKey
        }

        if (type === 'account') {
          accountForm.value.captchaKey = res.data.captchaKey
        } else {
          mobileForm.value.captchaKey = res.data.captchaKey
        }
      } else {
        ElMessage.error(
          `获取${type === 'account' ? '账号登录' : '手机号登录'}验证码失败`
        )
      }
    } catch (error) {
      ElMessage.error(
        `获取${
          type === 'account' ? '账号登录' : '手机号登录'
        }验证码异常，请重试`
      )
      console.error(`获取${type}类型验证码失败：`, error)
    }
  }

  const active = ref(false)
  const countdown = ref(60)
  let countdownTimer: ReturnType<typeof setInterval> | null = null
  const smsButtonText = computed(() => {
    console.log(`output->active`, active.value, countdown.value)
    return active.value ? `重新获取(${countdown.value}s)` : '获取验证码'
  })
  const clearCountdown = () => {
    if (countdownTimer) {
      clearInterval(countdownTimer)
      countdownTimer = null
    }
  }
  const resetCountdown = () => {
    clearCountdown()
    active.value = false
    countdown.value = 60
  }
  const startCountdown = () => {
    resetCountdown()
    active.value = true
    countdown.value = 60
    countdownTimer = setInterval(() => {
      if (countdown.value <= 1) {
        resetCountdown()
        return
      }
      countdown.value -= 1
    }, 1000)
  }
  const canGetCode = computed(() => {
    return (
      currentActiveName.value === 'phone' &&
      !active.value &&
      mobileForm.value.mobile &&
      mobileForm.value.captchaCode &&
      mobileForm.value.captchaKey
    )
  })

  const fetchSmsCode = async () => {
    if (!canGetCode.value) {
      ElMessage.warning('请完善手机号和图形验证码信息')
      return
    }
    active.value = true
    try {
      const res = await baseStore.postRequest(
        'auth/send-sms-code',
        {
          mobile: mobileForm.value.mobile,
          captchaCode: mobileForm.value.captchaCode,
          captchaKey: mobileForm.value.captchaKey
        },
        {
          needAuth: false,
          showSuccessMessage: true,
          successMessage: '验证码已发送，请注意查收'
        }
      )

      if (res.code !== 0) {
        message.error(res.msg || '发送验证码失败')
        await fetchCaptcha('phone')
        return
      }
      startCountdown()
    } catch (error) {
      console.error('发送短信验证码失败：', error)
      resetCountdown()
      await fetchCaptcha('phone')
    }
  }

  onMounted(async () => {
    if (loginStore?.consumeSkipLoginAutoCaptcha?.()) {
      return
    }

    await fetchCaptcha(currentActiveName.value)
  })

  const handleSubmit = async () => {
    if (!agreementChecked.value) {
      notification.warning({
        content: '请先阅读并同意用户协议和隐私协议',
        meta: '温馨提示',
        duration: 2500
      })
      return
    }

    let loginSuccess = false
    loading.value = true
    try {
      if (!hpFormRef.value) return
      const isValid = await hpFormRef.value.validate()
      if (isValid) {
        const res = await loginStore.loginAccountAction(formData.value)
        if (res.success) {
          loginSuccess = true
          const redirectPath = loginStore.redirectPath
          const targetPath =
            redirectPath &&
            !['/login', '/register', '/register-invite'].includes(
              redirectPath.split('?')[0]
            )
              ? redirectPath
              : '/home'
          loginStore.setRedirectPath('')

          await router.replace(targetPath)
          loginStore.startPostLoginReminders()
        } else {
          await fetchCaptcha(currentActiveName.value)
        }
      }
    } catch (err) {
      console.log('表单校验失败', err)
    } finally {
      if (!loginSuccess) {
        loading.value = false
      }
    }
  }

  const handleTab = async (activeName: LoginType) => {
    currentActiveName.value = activeName
    await fetchCaptcha(activeName)
  }
  onUnmounted(() => {
    clearCountdown()
  })
  return {
    hpFormRef,
    currentActiveName,
    rules,
    formData,
    formItems,
    currentCaptchaImg,
    fetchCaptcha,
    fetchSmsCode,
    active,
    agreementChecked,
    handleSubmit,
    handleTab,
    loading,
    smsButtonText
  }
}
